import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Coin, X, CheckCircle, Lightning } from "@phosphor-icons/react";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function PricingModal({ open, onClose, onPurchased }) {
  const [packs, setPacks] = useState([]);
  const [loadingPack, setLoadingPack] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    if (!open) return;
    apiClient.get("/packs").then(({ data }) => setPacks(data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const buy = async (pack_id) => {
    setLoadingPack(pack_id);
    try {
      const origin_url = window.location.origin;
      const { data } = await apiClient.post("/payments/checkout", { pack_id, origin_url });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoadingPack(null);
    }
  };

  return (
    <div
      data-testid="pricing-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl rounded-2xl border border-white/10 bg-[color:var(--surface)] p-8"
      >
        <button
          data-testid="pricing-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/5 text-white/60"
        >
          <X size={18} />
        </button>
        <div className="text-center mb-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-white/40 mb-2">{t("creditPacks")}</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
            {t("buyMore")} <span className="italic font-light">{t("creditsItalic")}</span>
          </h2>
          <p className="text-white/60 text-sm mt-2">{t("pricingSub")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {packs.map((p, idx) => {
            const featured = p.pack_id === "pro";
            const perCredit = (p.price / p.credits).toFixed(3);
            return (
              <div
                key={p.pack_id}
                data-testid={`pack-card-${p.pack_id}`}
                className={`relative rounded-xl border p-6 flex flex-col ${
                  featured
                    ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5"
                    : "border-white/10 bg-[color:var(--surface-2)]"
                }`}
              >
                {featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[color:var(--accent)] text-white text-[10px] font-mono-num uppercase tracking-widest">
                    {t("mostPopular")}
                  </span>
                )}
                <div className="flex items-center gap-2 mb-1">
                  {featured ? <Lightning size={20} weight="fill" className="text-[color:var(--accent)]" /> : <Coin size={20} weight="fill" className="text-[color:var(--warning)]" />}
                  <h3 className="font-display font-semibold text-xl">{p.name}</h3>
                </div>
                <div className="flex items-baseline gap-1 mt-4">
                  <span className="font-display font-bold text-4xl">${p.price.toFixed(0)}</span>
                  <span className="text-white/40 text-sm">{t("oneTime")}</span>
                </div>
                <p className="text-sm text-white/70 mt-2">
                  <span className="font-mono-num text-white">{p.credits}</span> credits
                  <span className="text-white/40 text-xs ml-2">(${perCredit}/sound)</span>
                </p>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {[
                    `${p.credits} sound generations`,
                    "All categories",
                    "Library + Multi-track mixer",
                    "MP3 / WAV export",
                    idx > 0 ? "Priority generation" : null,
                  ].filter(Boolean).map((f) => (
                    <li key={f} className="flex items-center gap-2 text-white/70">
                      <CheckCircle size={16} weight="fill" className="text-[color:var(--success)] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`buy-${p.pack_id}-btn`}
                  onClick={() => buy(p.pack_id)}
                  disabled={loadingPack === p.pack_id}
                  className={`mt-6 py-3 rounded-full font-medium text-sm transition-colors ${
                    featured
                      ? "btn-primary"
                      : "bg-white/5 border border-white/10 hover:bg-white/10 text-white"
                  }`}
                >
                  {loadingPack === p.pack_id ? t("redirecting") : `${t("buyPack")} ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-white/40 mt-6 font-mono-num uppercase tracking-widest">
          {t("secureCheckout")}
        </p>
      </div>
    </div>
  );
}
